import { isNil } from 'lodash';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router';

import VendorCveStore from 'app/store/VendorCveStore';
import usePageMeta from 'hooks/use-page-meta';
import useSearchParams from 'hooks/use-search-params';
import useStores from 'hooks/use-stores';
import Heading from 'main/components/Heading/Heading';
import Breadcrumbs from 'pages/Search/components/Breadcrumbs';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import RedirectLink from 'pages/Search/components/RedirectLink';
import { NONE, ROUTES } from 'pages/Search/constants';
import { noneIf, normalizeName } from 'pages/Search/utils';
import Utils from 'utils/Utils';

import useStyles from './styles';

const Vendor = () => {
    const history = useHistory();
    const { classes } = useStyles();

    const { vendorCveStore } = useStores();

    /** @type {Record<string, string>} */
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const page = Number(params.page || 1);
    const vendorName = params.vendorName;

    const pageSize = Number(searchParams.pageSize || 100);
    const score = searchParams.score ? Number(searchParams.score) : null;
    const year = searchParams.year ? Number(searchParams.year) : null;
    const month = searchParams.month ? Number(searchParams.month) : null;

    const normalizedVendorName = normalizeName(vendorName);

    const hasMetaPagination = Number(page || 1) > 1;

    usePageMeta({
        title: hasMetaPagination
            ? `${normalizedVendorName} vulnerabilities - Page ${page} | Vulners.com`
            : `${normalizedVendorName} vulnerabilities | Vulners.com`,
        description: hasMetaPagination
            ? `${normalizedVendorName} - Page ${page}. Here you can find all recent ${normalizedVendorName} security vulnerabilities from different products`
            : `Here you can find all recent ${normalizedVendorName} security vulnerabilities from different products`
    });

    /** @type {React.RefObject<{scrollTop: () => void}>} */
    const layoutRef = useRef(null);

    const setPath = useCallback(
        ({ page }) => history.push({ pathname: ROUTES.VENDOR({ page, vendorName }), search: history.location.search }),
        [history, vendorName]
    );

    const getPaginationHref = useCallback(
        (page) => ROUTES.VENDOR({ page: noneIf(page, 1), vendorName }).concat(history.location.search),
        [history.location.search, vendorName]
    );

    const breadcrumbs = useMemo(
        () => [
            { label: 'Vulners', link: ROUTES.ROOT() },
            { label: 'Vendors', link: ROUTES.VENDORS({ page: null, letter: null }) },
            { label: `${normalizedVendorName} Security Vulnerabilities`, link: '', disabled: true }
        ],
        [normalizedVendorName]
    );

    useEffect(() => {
        layoutRef.current?.scrollTop();
        vendorCveStore
            .setParams({
                vendorName,
                page: Number(page || 1),
                pageSize: Number(pageSize || 100),
                score: score ? Number(score) : null,
                year: year ? Number(year) : null,
                month: month ? Number(month) : null
            })
            .loadItems();
    }, [month, page, pageSize, score, vendorCveStore, vendorName, year]);

    const tableData = useMemo(
        () => [
            ['Product Name', 'Vulnerabilities'],
            ...vendorCveStore.list.map(({ vendorName, productName, cveCount }) => [
                <RedirectLink
                    key={productName}
                    // @ts-ignore
                    to={ROUTES.VENDOR_PRODUCT({ vendorName, productName, page: null, score, year, month })}
                    label={normalizeName(productName) || NONE}
                    active={Boolean(productName)}
                />,
                cveCount
            ])
        ],
        [month, score, vendorCveStore.list, year]
    );

    const handlePageChange = useCallback((page) => setPath({ page }), [setPath]);
    const handlePageSizeChange = useCallback(
        (pageSize) =>
            // @ts-ignore
            setSearchParams({ ...searchParams, pageSize: noneIf(pageSize, VendorCveStore.DEFAULT_PAGE_SIZE) }),
        [searchParams, setSearchParams]
    );

    return (
        <Layout
            scrollable
            ref={layoutRef}
            main={
                <>
                    <Heading
                        text={[
                            `${normalizedVendorName} Security Vulnerabilities`,
                            [
                                isNil(score) ? '' : `CVSS Score ${score} - ${score + 1}`,
                                [month ? Utils.monthOfYear(month - 1) : '', year].filter(Boolean).join(' ')
                            ]
                                .filter(Boolean)
                                .join(' | ')
                        ]
                            .filter(Boolean)
                            .join(' - ')}
                    />

                    <Breadcrumbs items={breadcrumbs} className={classes.breadcrumbs} />

                    <DataTable
                        data={tableData}
                        loading={vendorCveStore.isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        page={vendorCveStore.page}
                        pageSize={vendorCveStore.pageSize}
                        totalCount={vendorCveStore.totalCount}
                    />
                </>
            }
        />
    );
};

export default observer(Vendor);
